import React, { useState, useRef, useEffect } from 'react';
import {
  Send,
  Calendar,
  Upload,
  X,
  Users,
  Check,
  Clock,
  Trash2,
  Repeat,
  FileImage,
  Loader2,
  AlertCircle,
} from 'lucide-react';
import { getText } from '@/translate/text';
import CustomDatePicker from './CustomDatePicker';
import { EstimatedRecipientsBar } from '@components/notificationCenter/EstimatedRecipientsBar.jsx';
import { notification_actions } from '@/constants/notificationsActions';
import { NotificationService } from '@/services/notificationService';

const mainHours = Array.from({ length: 24 }, (_, i) => {
  const hour24 = i.toString().padStart(2, '0');
  const period = i >= 12 ? 'PM' : 'AM';
  const hour12 = i % 12 === 0 ? 12 : i % 12;
  return {
    value: `${hour24}:00`,
    label: `${hour12}:00 ${period}`,
  };
});

const ContentStep = ({
  formData,
  onChange,
  setFormData,
  AUDIENCE_OPTIONS,
  estimatedUsers,
  estimateLoading,
  estimateError,
}) => {
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [ctaData, setCtaData] = useState({ ads: [], offers: [], categories: [] });
  const [loadingCtaData, setLoadingCtaData] = useState(false);
  const datePickerRef = useRef(null);

  const ctaOptions = [
    { id: notification_actions.HOME_SCREEN.id, label: 'Home' },
    { id: notification_actions.SUBSCRIPTION_SCREEN.id, label: 'Subscription Screen' },
    { id: notification_actions.OFFERS_AND_ADS_PAGE.id, label: 'Offers and ads page' },
    { id: notification_actions.OFFER_DETAILS_SCREEN.id, label: 'Specific Offer and ads' },
    { id: notification_actions.OFFER_CATEGORY_SCREEN.id, label: 'Specific offer category' },
    { id: notification_actions.EXTERNAL_URL.id, label: 'External URL' },
    { id: notification_actions.FRESHCHAT_BOT.id, label: 'Dru support' },
    { id: notification_actions.NOTIFICATION_SCREEN.id, label: 'Notifications' },
  ];

  const onRemoveFilter = (field) => {
    if (field === 'users') {
      setFormData((prev) => ({ ...prev, audience_list: [] }));
    } else {
      onChange(field, '');
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (datePickerRef.current && !datePickerRef.current.contains(event.target)) {
        setShowDatePicker(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    setLoadingCtaData(true);
    NotificationService.getCtaOptions()
      .then((data) => {
        console.log('CTA options data received:', data);
        setCtaData(data || { ads: [], offers: [], categories: [] });
      })
      .catch((err) => console.error('Error fetching CTA options:', err))
      .finally(() => setLoadingCtaData(false));
  }, []);

  const [uploadingFile, setUploadingFile] = useState(null);
  const fileInputRef = useRef(null);

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const fileId = Math.random().toString(36).substr(2, 9);

    onChange('imageError', null); // Clear previous errors

    const validTypes = ['image/jpeg', 'image/png', 'image/jpg'];
    if (!validTypes.includes(file.type)) {
      setUploadingFile({
        id: fileId,
        name: file.name,
        size: file.size,
        error: 'Unsupported format. Please use PNG, JPG, or JPEG.',
        failed: true,
      });
      onChange('imageError', 'Unsupported format. Please use PNG, JPG, or JPEG.');
      return;
    }

    if (file.size > 2 * 1024 * 1024) {
      setUploadingFile({
        id: fileId,
        name: file.name,
        size: file.size,
        error: 'Max 2MB per image',
        failed: true,
      });
      onChange('imageError', 'Max 2MB per image');
      return;
    }

    // Set uploading state
    setUploadingFile({
      id: fileId,
      name: file.name,
      size: file.size,
      progress: 0,
      uploading: true,
    });

    // Simulate upload
    let progress = 0;
    const interval = setInterval(() => {
      progress += 10;
      setUploadingFile((prev) => (prev && prev.id === fileId ? { ...prev, progress } : prev));

      if (progress >= 100) {
        clearInterval(interval);
        setUploadingFile(null);
        setFormData((prev) => ({
          ...prev,
          image: file,
        }));
      }
    }, 200);

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const removeImage = () => {
    setFormData((prev) => ({ ...prev, image: null, imageError: null }));
  };

  const triggerReplace = () => {
    fileInputRef.current?.click();
  };

  const cancelUpload = () => {
    setUploadingFile(null);
    onChange('imageError', null);
  };

  return (
    <div className="space-y-12 p-8">
      {/* 1- Audience Box */}
      <section className="space-y-6">
        <h3 className="text-lg font-bold text-gray-800 dark:text-gray-200">1- Audience</h3>

        <EstimatedRecipientsBar
          formData={formData}
          audienceOptions={AUDIENCE_OPTIONS}
          estimatedUsers={estimatedUsers}
          estimateLoading={estimateLoading}
          estimateError={estimateError}
          onRemoveFilter={onRemoveFilter}
          enableRemoveFilter={false}
        />
      </section>

      <section className="space-y-6">
        <h3 className="text-lg font-bold text-gray-800 dark:text-gray-200">2- Content</h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <button
            type="button"
            onClick={() => onChange('scheduling_type', 'instant')}
            className={`flex items-start gap-4 p-4 rounded-xl border-2 transition-all text-[#6B7280] ${
              formData.scheduling_type === 'instant'
                ? 'border-[#008236] bg-[#F0FDF4] dark:bg-emerald-900/20'
                : 'border-gray-100 dark:border-gray-700 hover:border-[#F0FDF4] dark:hover:border-emerald-800'
            }`}
          >
            <div className={`p-2 rounded-lg text-gray-500`}>
              <Send className="w-5 h-5 text-[#6A7282]" />
            </div>
            <div className="text-left">
              <p className="font-bold text-[#6B7280]">Send Instantly</p>
              <p className="text-xs text-[#6B7280]">Send notification immediately</p>
            </div>
          </button>

          <button
            type="button"
            onClick={() => onChange('scheduling_type', 'scheduled')}
            className={`flex items-start gap-4 p-4 rounded-xl border-2 transition-all ${
              formData.scheduling_type === 'scheduled'
                ? 'border-[#008236] bg-[#F0FDF4] dark:bg-emerald-900/20'
                : 'border-gray-100 dark:border-gray-700 hover:border-[#F0FDF4] dark:hover:border-emerald-800'
            }`}
          >
            <div className={`p-2 rounded-lg text-gray-500`}>
              <Calendar className="w-5 h-5 text-[#6A7282]" />
            </div>
            <div className="text-left">
              <p className="font-bold text-[#6B7280]">Schedule for Later</p>
              <p className="text-xs text-[#6B7280]">Choose a specific date and time</p>
            </div>
          </button>
        </div>

        <div className="space-y-3">
          <label className="text-sm font-semibold text-gray-700 dark:text-gray-300">Language</label>
          <div className="flex gap-6">
            {[
              { id: 'both', label: 'Both Languages' },
              { id: 'en', label: 'English' },
              { id: 'ar', label: 'Arabic' },
            ].map((lang) => (
              <label key={lang.id} className="flex items-center gap-2 cursor-pointer group">
                <input
                  type="radio"
                  name="language"
                  value={lang.id}
                  checked={formData.language === lang.id}
                  onChange={(e) => onChange('language', e.target.value)}
                  className="w-4 h-4 text-[#008236] border-gray-300 focus:ring-[#008236]"
                />
                <span
                  className={`text-sm ${formData.language === lang.id ? 'text-gray-900 dark:text-gray-100 font-medium' : 'text-gray-500 group-hover:text-gray-700'}`}
                >
                  {lang.label}
                </span>
              </label>
            ))}
          </div>
        </div>

        {formData.scheduling_type === 'scheduled' && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4 animate-in fade-in slide-in-from-top-2 duration-300 relative">
            {/* Date Picker Trigger */}
            <div className="space-y-1 relative" ref={datePickerRef}>
              <label className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                Date <span className="text-red-500">*</span>
              </label>
              <button
                type="button"
                onClick={() => setShowDatePicker(!showDatePicker)}
                className="w-full flex items-center justify-between px-4 py-3 rounded-xl border border-gray-200 dark:border-gray-700 dark:bg-gray-800 dark:text-white focus:ring-2 focus:ring-[#008236] focus:border-[#008236] transition-all outline-none text-left"
              >
                <span dir="ltr" className="text-[#6B7280]">
                  {formData.schedule_date
                    ? formData.schedule_date.split('-').reverse().join('/')
                    : 'Select date'}
                </span>
                <Calendar className="w-5 h-5 text-gray-400" />
              </button>
              {showDatePicker && (
                <div className="absolute top-full left-0 mt-2 z-50">
                  <CustomDatePicker
                    value={formData.schedule_date}
                    onChange={(date) => onChange('schedule_date', date)}
                    onClose={() => setShowDatePicker(false)}
                  />
                </div>
              )}
            </div>

            <div className="space-y-1">
              <label className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                Time (Egypt) <span className="text-red-500">*</span>
              </label>
              <div className="relative text-[#6B7280]">
                <select
                  name="schedule_time"
                  value={formData.schedule_time || ''}
                  onChange={(e) => onChange('schedule_time', e.target.value)}
                  className="w-full px-4 py-3 rounded-xl border border-gray-200 dark:border-gray-700 dark:bg-gray-800 dark:text-white focus:ring-2 focus:ring-[#008236] focus:border-[#008236] transition-all outline-none appearance-none bg-no-repeat pr-10"
                  style={{
                    backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`,
                    backgroundPosition: 'right 1rem center',
                    backgroundSize: '1.5em 1.5em',
                  }}
                >
                  <option value="" disabled>
                    Select time
                  </option>
                  {mainHours.map((hour) => (
                    <option key={hour.value} value={hour.value}>
                      {hour.label}
                    </option>
                  ))}
                </select>
              </div>
              {formData.schedule_time &&
                (() => {
                  const [hours] = formData.schedule_time.split(':').map(Number);
                  const saudiHour = (hours + 1) % 24;
                  const saudiPeriod = saudiHour >= 12 ? 'PM' : 'AM';
                  const saudi12 = saudiHour % 12 === 0 ? 12 : saudiHour % 12;

                  const uaeHour = (hours + 2) % 24;
                  const uaePeriod = uaeHour >= 12 ? 'PM' : 'AM';
                  const uae12 = uaeHour % 12 === 0 ? 12 : uaeHour % 12;

                  return (
                    <p className="text-[12px] text-[#4094F7] mt-1">
                      The Time will be {saudi12}:00 {saudiPeriod} in Saudi Arabia and {uae12}:00{' '}
                      {uaePeriod} in the UAE.
                    </p>
                  );
                })()}
            </div>
          </div>
        )}

        <div className="space-y-8">
          {(formData.language === 'en' || formData.language === 'both') && (
            <div className="space-y-4">
              <div className="space-y-1">
                <label className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                  Title <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <input
                    type="text"
                    name="title_en"
                    value={formData.title_en}
                    onChange={(e) => {
                      if (e.target.value.length <= 60) onChange(e);
                    }}
                    placeholder="e.g., Limited Time Offer!"
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 dark:border-gray-700 dark:bg-gray-800 dark:text-white focus:ring-2 focus:ring-[#008236] focus:border-[#008236] transition-all outline-none"
                  />
                  <span className="absolute left-4 bottom-[-20px] text-[10px] text-gray-400">
                    {formData.title_en?.length || 0}/60 characters
                  </span>
                </div>
              </div>

              <div className="space-y-1 pt-2">
                <label className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                  Message <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <textarea
                    name="message_en"
                    value={formData.message_en}
                    onChange={(e) => {
                      if (e.target.value.length <= 200) onChange(e);
                    }}
                    placeholder="Write your notification message here..."
                    rows={4}
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 dark:border-gray-700 dark:bg-gray-800 dark:text-white focus:ring-2 focus:ring-[#008236] focus:border-[#008236] transition-all outline-none resize-none"
                  />
                  <span className="absolute left-4 bottom-[-20px] text-[10px] text-gray-400">
                    {formData.message_en?.length || 0}/200 characters
                  </span>
                </div>
              </div>
            </div>
          )}

          {(formData.language === 'ar' || formData.language === 'both') && (
            <div className="space-y-4" dir="rtl">
              <div className="space-y-1">
                <label className="text-sm font-semibold text-gray-700 dark:text-gray-300 text-right block">
                  عنوان الإشعار <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <input
                    type="text"
                    name="title_ar"
                    value={formData.title_ar}
                    onChange={(e) => {
                      if (e.target.value.length <= 60) onChange(e);
                    }}
                    placeholder="مثال: حان وقت الفحص الطبي!"
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 dark:border-gray-700 dark:bg-gray-800 dark:text-white focus:ring-2 focus:ring-[#008236] focus:border-[#008236] transition-all outline-none text-right placeholder:text-right"
                  />
                  <span className="absolute right-4 bottom-[-20px] text-[10px] text-gray-400">
                    {formData.title_ar?.length || 0}/60 characters
                  </span>
                </div>
                <p className="text-xs text-gray-400 text-left mt-6">اجعله قصيراً وجذاباً</p>
              </div>

              <div className="space-y-1 pt-2">
                <label className="text-sm font-semibold text-gray-700 dark:text-gray-300 text-right block">
                  نص الرسالة <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <textarea
                    name="message_ar"
                    value={formData.message_ar}
                    onChange={(e) => {
                      if (e.target.value.length <= 150) onChange(e);
                    }}
                    placeholder="أدخل المحتوى الرئيسي لرسالتك هنا..."
                    rows={4}
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 dark:border-gray-700 dark:bg-gray-800 dark:text-white focus:ring-2 focus:ring-[#008236] focus:border-[#008236] transition-all outline-none resize-none text-right placeholder:text-right"
                  />
                  <span className="absolute right-4 bottom-[-20px] text-[10px] text-gray-400">
                    {formData.message_ar?.length || 0}/150 characters
                  </span>
                </div>
                <p className="text-xs text-gray-400 text-left mt-6">رسالة واضحة وقابلة للتنفيذ</p>
              </div>
            </div>
          )}
        </div>

        <div className="space-y-4">
          <label className="text-sm font-semibold text-gray-700 dark:text-gray-300">
            Image (Optional)
          </label>

          {/* Upload Dropzone - Hidden when an image exists or is uploading (optional, but usually kept if they want to replace) */}
          <div className="relative border-2 border-dashed border-gray-200 dark:border-gray-700 rounded-xl p-8 transition-all hover:border-[#008236] group cursor-pointer">
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleImageChange}
              accept="image/png, image/jpeg, image/jpg"
              className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
            />
            <div className="flex flex-col items-center justify-center gap-2">
              <div className="p-3 bg-gray-50 dark:bg-gray-800 rounded-full text-gray-400 group-hover:text-[#008236] transition-colors">
                <Upload className="w-8 h-8" />
              </div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                Click to upload image
              </p>
              <p className="text-xs text-gray-400">PNG, JPG up to 2MB</p>
            </div>
          </div>

          <div className="space-y-3 mt-4">
            {/* Uploading Progress Block */}
            {uploadingFile && (
              <div className="relative p-4 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
                <div className="flex items-center gap-4 mb-3">
                  <div className="w-10 h-10 rounded-lg bg-[#E5F1FF] flex items-center justify-center flex-shrink-0">
                    <FileImage className="w-6 h-6 text-[#4094F7]" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <p className="text-sm font-bold text-gray-900 dark:text-white truncate">
                        {uploadingFile.name}
                      </p>
                      <button
                        type="button"
                        onClick={cancelUpload}
                        className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors"
                      >
                        <div className="w-6 h-6 flex items-center justify-center rounded-full bg-gray-200 dark:bg-gray-700">
                          <X className="w-4 h-4 text-gray-500 dark:text-gray-300 stroke-[3]" />
                        </div>
                      </button>
                    </div>
                    {!uploadingFile.failed && (
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-xs text-gray-400">
                          {(uploadingFile.size / 1024).toFixed(0)}kb
                        </span>
                      </div>
                    )}
                    {uploadingFile.failed && (
                      <p className="text-xs text-red-500 mt-1">{uploadingFile.error}</p>
                    )}
                  </div>
                </div>
                {!uploadingFile.failed && (
                  <div className="flex items-center gap-3">
                    <div className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-[#00A63E] transition-all duration-300"
                        style={{ width: `${uploadingFile.progress}%` }}
                      ></div>
                    </div>
                    <span className="text-xs text-gray-400 w-8">{uploadingFile.progress}%</span>
                  </div>
                )}
              </div>
            )}

            {/* Uploaded Image Block */}
            {formData.image && (
              <div className="relative p-4 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 rounded-lg bg-gray-100 dark:bg-gray-900 overflow-hidden flex-shrink-0">
                    <img
                      src={
                        typeof formData.image === 'string'
                          ? formData.image
                          : URL.createObjectURL(formData.image)
                      }
                      alt="Preview"
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-bold text-gray-900 dark:text-white truncate">
                      {formData.image.name || 'Uploaded Image'}
                    </p>
                    <p className="text-xs text-gray-400 mt-0.5">
                      {formData.image.size
                        ? `${(formData.image.size / 1024).toFixed(0)}kb`
                        : 'URL Source'}
                    </p>
                  </div>
                  <div className="flex items-center gap-3">
                    <button
                      type="button"
                      onClick={removeImage}
                      className="p-1.5 rounded-lg hover:bg-red-50 text-gray-400 hover:text-red-500 transition-colors"
                    >
                      <Trash2 className="w-5 h-5" />
                    </button>
                    <button
                      type="button"
                      onClick={triggerReplace}
                      className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-[#008236] transition-colors"
                    >
                      <Repeat className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="space-y-4">
          <label className="text-sm font-semibold text-gray-700 dark:text-gray-300">
            Call to Action
          </label>
          <div className="relative">
            <select
              name="cta"
              value={formData.cta || ''}
              onChange={onChange}
              className="w-full px-4 py-3 rounded-xl border border-gray-200 dark:border-gray-700 dark:bg-gray-800 dark:text-white focus:ring-2 focus:ring-[#008236] focus:border-[#008236] transition-all outline-none appearance-none bg-no-repeat pr-10"
              style={{
                backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`,
                backgroundPosition: 'right 1rem center',
                backgroundSize: '1.5em 1.5em',
              }}
            >
              <option value="" disabled>
                Select an action
              </option>
              {ctaOptions.map((action) => (
                <option key={action.id} value={action.id}>
                  {action.label}
                </option>
              ))}
            </select>
          </div>

          {Number(formData.cta) === notification_actions.OFFER_DETAILS_SCREEN.id && (
            <div className="space-y-1 animate-in fade-in slide-in-from-top-2 duration-300">
              <label className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                Select Offer / Ad <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <select
                  name="cta_detail"
                  value={formData.cta_offer_id || formData.cta_ad_id || ''}
                  onChange={(e) => {
                    const val = e.target.value;
                    const isAd = ctaData.ads.some((ad) => ad._id === val);
                    setFormData((prev) => ({
                      ...prev,
                      cta_offer_id: isAd ? '' : val,
                      cta_ad_id: isAd ? val : '',
                    }));
                  }}
                  disabled={loadingCtaData}
                  className="w-full px-4 py-3 rounded-xl border border-gray-200 dark:border-gray-700 dark:bg-gray-800 dark:text-white focus:ring-2 focus:ring-[#008236] focus:border-[#008236] transition-all outline-none appearance-none bg-no-repeat pr-10 disabled:opacity-50"
                  style={{
                    backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`,
                    backgroundPosition: 'right 1rem center',
                    backgroundSize: '1.5em 1.5em',
                  }}
                >
                  <option value="" disabled>
                    {loadingCtaData ? 'Loading options...' : 'Select an offer or ad'}
                  </option>
                  <optgroup label="Offers">
                    {ctaData.offers.map((offer) => (
                      <option key={offer._id} value={offer._id}>
                        {offer.titleEn}
                      </option>
                    ))}
                  </optgroup>
                  <optgroup label="Ads">
                    {ctaData.ads.map((ad) => (
                      <option key={ad._id} value={ad._id}>
                        {ad.titleEn}
                      </option>
                    ))}
                  </optgroup>
                </select>
                {loadingCtaData && (
                  <div className="absolute right-10 top-1/2 -translate-y-1/2">
                    <Loader2 className="w-4 h-4 animate-spin text-gray-400" />
                  </div>
                )}
              </div>
            </div>
          )}

          {Number(formData.cta) === notification_actions.OFFER_CATEGORY_SCREEN.id && (
            <div className="space-y-1 animate-in fade-in slide-in-from-top-2 duration-300">
              <label className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                Select Category <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <select
                  name="cta_category_id"
                  value={formData.cta_category_id || ''}
                  onChange={onChange}
                  disabled={loadingCtaData}
                  className="w-full px-4 py-3 rounded-xl border border-gray-200 dark:border-gray-700 dark:bg-gray-800 dark:text-white focus:ring-2 focus:ring-[#008236] focus:border-[#008236] transition-all outline-none appearance-none bg-no-repeat pr-10 disabled:opacity-50"
                  style={{
                    backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`,
                    backgroundPosition: 'right 1rem center',
                    backgroundSize: '1.5em 1.5em',
                  }}
                >
                  <option value="" disabled>
                    {loadingCtaData ? 'Loading categories...' : 'Select a category'}
                  </option>
                  {ctaData.categories.map((cat) => (
                    <option key={cat._id} value={cat._id}>
                      {cat.categoryNameEn}
                    </option>
                  ))}
                </select>
                {loadingCtaData && (
                  <div className="absolute right-10 top-1/2 -translate-y-1/2">
                    <Loader2 className="w-4 h-4 animate-spin text-gray-400" />
                  </div>
                )}
              </div>
            </div>
          )}

          {Number(formData.cta) === notification_actions.EXTERNAL_URL.id && (
            <div className="space-y-1 animate-in fade-in slide-in-from-top-2 duration-300">
              <label className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                External URL Link <span className="text-red-500">*</span>
              </label>
              <input
                type="url"
                name="cta_url"
                value={formData.cta_url || ''}
                onChange={onChange}
                placeholder="https://example.com"
                className="w-full px-4 py-3 rounded-xl border border-gray-200 dark:border-gray-700 dark:bg-gray-800 dark:text-white focus:ring-2 focus:ring-[#008236] focus:border-[#008236] transition-all outline-none"
              />
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default ContentStep;
